import type { ReactNode } from 'react';
import { Card, Typography } from 'antd';
import { ToolOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@features/auth/hooks/useAuth';

const { Title, Paragraph } = Typography;

export interface MigrationPlaceholderProps {
  /** Ключ заголовка раздела, обычно из namespace menu (например `menu:tariffs`) */
  titleKey: string;
  /** Иконка раздела — та же, что у пункта меню, чтобы оператор узнал, куда попал */
  icon?: ReactNode;
  /**
   * Ключ описания: чего именно нет в V5, от вопроса оператора к разделу.
   * По умолчанию — общее «раздел ещё не перенесён».
   */
  descriptionKey?: string;
  /** Ключ пояснения, на какой вопрос отвечает соседний экран с частью данных */
  relatedNoteKey?: string;
  /**
   * Соседние экраны с частью данных. Список, а не одна ссылка: у раздела v4 могло
   * не быть ровно одного наследника в V5 — «Склады» распались на номенклатуру и
   * поставщиков, и назвать оператору только первый значило бы спрятать второй.
   */
  related?: ReadonlyArray<MigrationPlaceholderLink>;
}

/** Ссылка на живой экран: путь + ключ подписи, тот же, что у его пункта меню. */
export interface MigrationPlaceholderLink {
  path: string;
  labelKey: string;
  /**
   * Права ЧТЕНИЯ целевого экрана. Достаточно ЛЮБОГО — ровно как на сервере, где
   * `RequirePermission` разрешает по первому совпавшему (middleware/auth.go:366-376);
   * каталог склада и поставщики так и загейчены парой inventory.view ∪ inventory.catalog.
   *
   * Поле ОБЯЗАТЕЛЬНОЕ, и это осознанно: необязательное молча пропустило бы
   * следующую ссылку без гейта, а незагейченная ссылка — это отправить оператора
   * в 403 своими руками, подписавшись под «вот куда идти сейчас». Пусть лучше не
   * скомпилируется.
   */
  permissions: readonly string[];
}

/**
 * MigrationPlaceholder — экран раздела, ещё не перенесённого из v4.
 *
 * Пункт меню на такой раздел остаётся видимым: оператор должен понимать, что
 * раздел существует и где он появится, а не упираться в молчаливый редирект на
 * главную. Заглушка ставится ТОЛЬКО туда, где функции в V5 действительно нет —
 * если действие уже доступно на другом экране, пункт меню ведут на него.
 *
 * ССЫЛКИ ФИЛЬТРУЮТСЯ ПРАВОМ НА ЦЕЛЕВОЙ ЭКРАН. Сама заглушка достижима шире, чем
 * экраны, на которые она показывает: исторически `/settings/warehouse` не имел
 * собственного гейта, а каталог и поставщики склада требуют
 * inventory.view ∪ inventory.catalog. Без фильтра оператор без этих прав читал бы
 * «вот куда идти сейчас» и упирался в 403 — то есть мы бы сами отправили его в
 * тупик и подписались под этим. Предикат тот же, что фильтрует сайдбар
 * (`useAuth().hasPermission`, см. useSidebarMenu.ts).
 *
 * ТЕКСТ от прав НЕ зависит: он объясняет, чего в V5 нет, и верен при любых правах.
 * Скрывается только ссылка. Если не осталось ни одной — блок не рисуется вовсе,
 * пустого заголовка «Куда идти сейчас» быть не должно.
 */
export function MigrationPlaceholder({
  titleKey,
  icon,
  descriptionKey,
  relatedNoteKey,
  related,
}: MigrationPlaceholderProps) {
  const { t } = useTranslation(['common', 'menu']);
  const { hasPermission } = useAuth();

  // Любого из прав достаточно — как RequirePermission на сервере.
  const visibleRelated = (related ?? []).filter((link) =>
    link.permissions.some((permission) => hasPermission(permission)),
  );

  return (
    <Card>
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ fontSize: 64, color: '#1890ff', marginBottom: 24 }}>
          {icon ?? <ToolOutlined />}
        </div>
        <Title level={2}>{t(titleKey)}</Title>
        <Paragraph type="secondary" style={{ maxWidth: 640, margin: '0 auto 8px' }}>
          {t(descriptionKey ?? 'common:migration_placeholder.description')}
        </Paragraph>
        {relatedNoteKey && (
          <Paragraph type="secondary" style={{ maxWidth: 640, margin: '0 auto 8px' }}>
            {t(relatedNoteKey)}
          </Paragraph>
        )}
        {visibleRelated.length > 0 && (
          <Paragraph>
            {t('common:migration_placeholder.related_prefix')}{' '}
            {visibleRelated.map((link, index) => (
              <span key={link.path}>
                {index > 0 && ', '}
                <Link to={link.path}>{t(link.labelKey)}</Link>
              </span>
            ))}
          </Paragraph>
        )}
        <Paragraph type="secondary">{t('common:migration_placeholder.hint')}</Paragraph>
      </div>
    </Card>
  );
}
